import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PropertyCard from "@/components/PropertyCard";
import EditableImage from "@/components/EditableImage";
import { getAuthHeaders } from "@/contexts/AuthContext";
import { Bed, Bath, Ruler, Users, LayoutGrid, ChevronLeft, ChevronRight, X, MapPin, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyDetail() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [inquiry, setInquiry] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  const reloadListing = useCallback(() => {
    axios.get(`${API}/listings/${slug}`).then(res => setListing(res.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/listings/${slug}`).then(res => {
      setListing(res.data);
    }).catch(() => {
      setListing(null);
    }).finally(() => setLoading(false));

    axios.get(`${API}/listings`).then(res => {
      setRelated(res.data.filter(l => l.slug !== slug).slice(0, 3));
    }).catch(() => {});
  }, [slug]);

  const replaceImage = async (field, url) => {
    if (!listing) return;
    await axios.put(`${API}/admin/listings/${listing.id}/replace-image`, {
      image_url: url, field
    }, { headers: getAuthHeaders(), withCredentials: true });
    reloadListing();
  };

  const allImages = listing ? [listing.featured_image, ...(listing.gallery_images || [])].filter(Boolean) : [];

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (!inquiry.name || !inquiry.email || !inquiry.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...inquiry,
        property_id: listing.id,
        property_title: listing.title,
      });
      toast.success("Inquiry submitted! We'll be in touch soon.");
      setInquiry({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white pt-20">
      <div className="w-8 h-8 border-2 border-[#32A334] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!listing) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white pt-20">
      <h2 className="font-['DM_Serif_Display'] text-3xl text-[#1A1A2E] mb-4">Property Not Found</h2>
      <Link to="/properties" className="text-[#32A334] text-sm uppercase tracking-[0.15em]">Back to Properties</Link>
    </div>
  );

  const stats = [
    { icon: Bed, label: "Bedrooms", value: listing.bedrooms },
    { icon: Bath, label: "Bathrooms", value: listing.bathrooms },
    { icon: Ruler, label: "Sq Ft", value: listing.square_feet?.toLocaleString() },
    { icon: LayoutGrid, label: "Beds", value: listing.beds },
    { icon: Users, label: "Sleeps", value: listing.sleeps },
  ];

  return (
    <div data-testid="property-detail-page" className="bg-white pt-20">
      {/* Lightbox */}
      {lightbox.open && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" data-testid="lightbox">
          <button onClick={() => setLightbox({ open: false, index: 0 })} className="absolute top-6 right-6 text-white/70 hover:text-white" data-testid="lightbox-close">
            <X className="w-8 h-8" />
          </button>
          <button
            onClick={() => setLightbox(l => ({ ...l, index: (l.index - 1 + allImages.length) % allImages.length }))}
            className="absolute left-6 text-white/70 hover:text-white"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <img src={allImages[lightbox.index]} alt="" className="max-w-[90vw] max-h-[85vh] object-contain" />
          <button
            onClick={() => setLightbox(l => ({ ...l, index: (l.index + 1) % allImages.length }))}
            className="absolute right-6 text-white/70 hover:text-white"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          <div className="absolute bottom-6 text-white/50 text-sm">{lightbox.index + 1} / {allImages.length}</div>
        </div>
      )}

      {/* Image Gallery */}
      <section className="px-6 md:px-12 lg:px-24 py-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 max-h-[70vh] overflow-hidden rounded-lg" data-testid="image-gallery">
          <div className="md:col-span-8 aspect-[16/10] cursor-pointer overflow-hidden">
            <EditableImage src={listing.featured_image} alt={listing.title} onReplace={(url) => replaceImage("featured_image", url)}>
              <img
                src={listing.featured_image}
                alt={listing.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                onClick={() => setLightbox({ open: true, index: 0 })}
              />
            </EditableImage>
          </div>
          <div className="md:col-span-4 grid grid-cols-2 gap-2">
            {allImages.slice(1, 5).map((img, i) => (
              <div key={i} className="aspect-square cursor-pointer overflow-hidden relative">
                <EditableImage src={img} alt="" onReplace={(url) => replaceImage(`gallery_images.${i}`, url)}>
                  <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" onClick={() => setLightbox({ open: true, index: i + 1 })} />
                </EditableImage>
                {i === 3 && allImages.length > 5 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-sm font-semibold pointer-events-none">
                    +{allImages.length - 5} more
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Header */}
      <section className="px-6 md:px-12 lg:px-24 py-8 border-b border-[#E2E8F0]">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <h1 data-testid="property-title" className="font-['DM_Serif_Display'] text-4xl sm:text-5xl tracking-tight text-[#1A1A2E] mb-3">
              {listing.title}
            </h1>
            {listing.location && (
              <p className="flex items-center gap-2 text-[#64748B] text-sm">
                <MapPin className="w-4 h-4 text-[#32A334]" /> {listing.location}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-3">
            <span className="text-[#64748B] text-xs">Managed by Bergrand Realty</span>
            <div className="flex gap-3 flex-wrap">
            {listing.airbnb_url && (
              <a
                href={listing.airbnb_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-airbnb-link"
                className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-6 py-3 uppercase tracking-widest text-xs font-semibold inline-flex items-center gap-2 rounded"
              >
                Book on Airbnb <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            {listing.vrbo_url && (
              <a
                href={listing.vrbo_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-vrbo-link"
                className="border border-[#1C671D] text-[#1C671D] hover:bg-[#1C671D] hover:text-white transition-colors duration-300 px-6 py-3 uppercase tracking-widest text-xs inline-flex items-center gap-2 rounded"
              >
                Book on VRBO <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="flex flex-wrap gap-8 mt-8 pt-8 border-t border-[#E2E8F0]" data-testid="property-stats">
          {stats.map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <s.icon className="w-5 h-5 text-[#32A334]" strokeWidth={1.5} />
              <div>
                <p className="text-[#1A1A2E] text-lg font-semibold font-['DM_Serif_Display']">{s.value}</p>
                <p className="text-[#94A3B8] text-xs uppercase tracking-[0.1em]">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
        {/* Left Content */}
        <div className="lg:col-span-8 border-r border-[#E2E8F0]">
          {/* Description */}
          <section className="px-6 md:px-12 lg:px-12 py-12 border-b border-[#E2E8F0]" data-testid="property-description">
            <h2 className="font-['DM_Serif_Display'] text-2xl sm:text-3xl text-[#1A1A2E] mb-6">About This Property</h2>
            <p className="text-[#64748B] text-base leading-relaxed whitespace-pre-line">{listing.full_description}</p>
          </section>

          {/* Amenities */}
          {listing.amenities?.length > 0 && (
            <section className="px-6 md:px-12 lg:px-12 py-12 border-b border-[#E2E8F0]" data-testid="property-amenities">
              <h2 className="font-['DM_Serif_Display'] text-2xl sm:text-3xl text-[#1A1A2E] mb-6">Amenities</h2>
              <div className="flex flex-wrap gap-2.5">
                {(showAllAmenities ? listing.amenities : listing.amenities.slice(0, 12)).map((a, i) => (
                  <span key={i} className="px-4 py-2 border border-[#E2E8F0] text-[#64748B] text-sm rounded hover:border-[#32A334] hover:text-[#32A334] transition-colors bg-[#F4F6FA]">
                    {a}
                  </span>
                ))}
              </div>
              {listing.amenities.length > 12 && (
                <button
                  onClick={() => setShowAllAmenities(!showAllAmenities)}
                  data-testid="toggle-amenities"
                  className="mt-4 text-[#1C671D] text-sm font-medium hover:text-[#145215] transition-colors"
                >
                  {showAllAmenities ? `Show Less` : `Show All ${listing.amenities.length} Amenities`}
                </button>
              )}
            </section>
          )}

          {/* House Rules */}
          {listing.house_rules && (
            <section className="px-6 md:px-12 lg:px-12 py-12 border-b border-[#E2E8F0]" data-testid="property-rules">
              <h2 className="font-['DM_Serif_Display'] text-2xl sm:text-3xl text-[#1A1A2E] mb-6">House Rules</h2>
              <p className="text-[#64748B] text-sm leading-relaxed whitespace-pre-line">{listing.house_rules}</p>
            </section>
          )}
        </div>

        {/* Right - Inquiry Form */}
        <div className="lg:col-span-4">
          <div className="sticky top-24 px-6 md:px-8 py-12">
            <h3 className="font-['DM_Serif_Display'] text-2xl text-[#1A1A2E] mb-2">Inquire About This Property</h3>
            <p className="text-[#94A3B8] text-sm mb-8">We'll get back to you within 24 hours.</p>
            <form onSubmit={handleInquiry} className="flex flex-col gap-6" data-testid="inquiry-form">
              <input
                type="text"
                placeholder="Name *"
                value={inquiry.name}
                onChange={(e) => setInquiry(f => ({ ...f, name: e.target.value }))}
                data-testid="inquiry-name"
                className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none transition-colors w-full placeholder:text-[#94A3B8] text-sm rounded"
                required
              />
              <input
                type="email"
                placeholder="Email *"
                value={inquiry.email}
                onChange={(e) => setInquiry(f => ({ ...f, email: e.target.value }))}
                data-testid="inquiry-email"
                className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none transition-colors w-full placeholder:text-[#94A3B8] text-sm rounded"
                required
              />
              <input
                type="tel"
                placeholder="Phone"
                value={inquiry.phone}
                onChange={(e) => setInquiry(f => ({ ...f, phone: e.target.value }))}
                data-testid="inquiry-phone"
                className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none transition-colors w-full placeholder:text-[#94A3B8] text-sm rounded"
              />
              <textarea
                placeholder="Message *"
                value={inquiry.message}
                onChange={(e) => setInquiry(f => ({ ...f, message: e.target.value }))}
                rows={4}
                data-testid="inquiry-message"
                className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none transition-colors w-full placeholder:text-[#94A3B8] text-sm resize-none rounded"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit"
                className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-8 py-3.5 uppercase tracking-widest text-sm font-semibold disabled:opacity-50 rounded"
              >
                {submitting ? "Sending..." : "Send Inquiry"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Related Properties */}
      {related.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-[#E2E8F0] bg-[#F4F6FA]" data-testid="related-properties">
          <h2 className="font-['DM_Serif_Display'] text-3xl sm:text-4xl tracking-tight text-[#1A1A2E] mb-12">More Properties</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {related.map(l => <PropertyCard key={l.id} listing={l} />)}
          </div>
        </section>
      )}
    </div>
  );
}
